/**
 * In-memory PNG slots for report thumbnails. Survives Report unmount when the
 * user leaves the tab, so returning does not recapture unless {@link contentKey}
 * or the visible page-key list changed.
 *
 * Module-scoped on purpose: React state in {@link useReportPageThumbnails}
 * is discarded on unmount. URLs are stored by page key so skip/unskip does not
 * remap later slides onto the wrong snapshot.
 */
export type ReportThumbnailCacheSnapshot = {
  urlsByPageKey: Record<string, string>;
  layoutSignature: string | undefined;
};

type ReportThumbnailCacheEntry = {
  contentKey: string;
  pageKeys: string[];
  thumbWidthPx: number;
  urlsByPageKey: Record<string, string>;
  layoutSignature: string | undefined;
};

let cacheEntry: ReportThumbnailCacheEntry | undefined;

const areSamePageKeys = (
  left: readonly string[],
  right: readonly string[],
): boolean =>
  left.length === right.length &&
  left.every((key, index) => key === right[index]);

/**
 * Cached thumbnail URLs when {@link contentKey}, {@link pageKeys}, and
 * {@link thumbWidthPx} match the last capture.
 */
export const getReportThumbnailCache = (
  contentKey: string,
  pageKeys: readonly string[],
  thumbWidthPx: number,
): ReportThumbnailCacheSnapshot | undefined => {
  if (!contentKey || pageKeys.length === 0) return;
  if (!cacheEntry) return;
  if (cacheEntry.contentKey !== contentKey) return;
  if (cacheEntry.thumbWidthPx !== thumbWidthPx) return;
  if (!areSamePageKeys(cacheEntry.pageKeys, pageKeys)) return;
  return {
    urlsByPageKey: { ...cacheEntry.urlsByPageKey },
    layoutSignature: cacheEntry.layoutSignature,
  };
};

/**
 * True when every visible page key has a non-empty data URL.
 */
export const hasCompleteReportThumbnailCache = (
  urlsByPageKey: Record<string, string> | undefined,
  pageKeys: readonly string[],
): boolean => {
  if (!urlsByPageKey || pageKeys.length === 0) return false;
  return pageKeys.every((key) => Boolean(urlsByPageKey[key]));
};

const replaceCacheBucket = (
  contentKey: string,
  pageKeys: readonly string[],
  thumbWidthPx: number,
): void => {
  cacheEntry = {
    contentKey,
    pageKeys: [...pageKeys],
    thumbWidthPx,
    urlsByPageKey: {},
    layoutSignature: undefined,
  };
};

const isCurrentBucket = (
  contentKey: string,
  pageKeys: readonly string[],
  thumbWidthPx: number,
): boolean =>
  !!cacheEntry &&
  cacheEntry.contentKey === contentKey &&
  cacheEntry.thumbWidthPx === thumbWidthPx &&
  areSamePageKeys(cacheEntry.pageKeys, pageKeys);

/**
 * Stores one captured thumbnail. Replaces the cache bucket when the key,
 * visible page list, or thumb width differs from the current entry.
 */
export const writeReportThumbnailCacheUrl = (
  contentKey: string,
  pageKeys: readonly string[],
  thumbWidthPx: number,
  pageKey: string,
  url: string,
): void => {
  if (!contentKey || pageKeys.length === 0 || !pageKey || thumbWidthPx <= 0) {
    return;
  }
  if (!pageKeys.includes(pageKey)) return;
  if (!isCurrentBucket(contentKey, pageKeys, thumbWidthPx)) {
    replaceCacheBucket(contentKey, pageKeys, thumbWidthPx);
  }
  if (!cacheEntry) return;
  cacheEntry = {
    ...cacheEntry,
    urlsByPageKey: { ...cacheEntry.urlsByPageKey, [pageKey]: url },
  };
};

/**
 * Records the page-box layout used for the cached snapshots so remount can
 * skip recapture when the main pane is still the same size.
 */
export const writeReportThumbnailCacheLayoutSignature = (
  contentKey: string,
  pageKeys: readonly string[],
  thumbWidthPx: number,
  layoutSignature: string,
): void => {
  if (!isCurrentBucket(contentKey, pageKeys, thumbWidthPx) || !cacheEntry) {
    return;
  }
  cacheEntry = { ...cacheEntry, layoutSignature };
};

/** Test-only: drop the module cache. */
export const clearReportThumbnailCache = (): void => {
  cacheEntry = undefined;
};
